// Pure helpers for the single-terminal face roster (access_control_face_roster.vue):
// terminal-connection detection, and a per-terminal, per-cursor PAGE cache with an
// injectable clock. Kept as a separate module (rather than reused from
// access_control_multi_face_roster.vue) because that component caches one MERGED
// whole-terminal traversal result per device, while this one caches individual
// PAGES so both ordinary page-by-page navigation and the "search/filter across all
// pages" traversal can reuse whatever has already been fetched, page by page.
//
// The connection-status detection mirrors access_control_multi_face_roster.vue's
// rosterConnectionStatus (same Hikvision "HTTP 200, status offline" contract); it
// is duplicated rather than imported so the two components stay fully independent
// and neither's tests are put at risk by shared runtime state.

export const FACE_ROSTER_PAGE_CACHE_TTL_MS = 60000
// Hikvision terminals reject a cursor query that arrives right after the previous
// one to the same terminal (HTTP 200, status "offline", reason "Terminal query
// failed"). Pace consecutive pages fetched during the all-pages traversal and
// retry an offline page with backoff before giving up on it.
export const FACE_ROSTER_PAGE_DELAY_MS = 1500
export const FACE_ROSTER_OFFLINE_RETRY_DELAYS_MS = [1500, 3000]

// Swappable clock (like access_control_multi_face_roster.vue's rosterCacheClock)
// so tests can move time forward deterministically instead of depending on the
// real wall clock.
export const faceRosterPageCacheClock = { now: () => Date.now() }

const pageCache = new Map()

const pageCacheKey = (deviceId, cursor) => `${deviceId}::${cursor === undefined || cursor === null ? '' : String(cursor)}`

export const clearFaceRosterPageCache = () => pageCache.clear()

export const cachedFaceRosterPage = (deviceId, cursor) => {
	const key = pageCacheKey(deviceId, cursor)
	const entry = pageCache.get(key)
	if (!entry) return null
	if (faceRosterPageCacheClock.now() - entry.storedAt >= FACE_ROSTER_PAGE_CACHE_TTL_MS) {
		pageCache.delete(key)
		return null
	}
	return entry.roster
}

export const faceRosterConnectionStatus = (roster) => {
	const rawStatus = roster && (roster.status || roster.terminalStatus || roster.connectionStatus || roster.deviceStatus)
	const normalizedStatus = String(rawStatus || '').trim().toLowerCase()
	if (['online', 'connected', 'ok', 'ready'].includes(normalizedStatus)) return 'online'
	if (['offline', 'disconnected', 'unreachable', 'unavailable'].includes(normalizedStatus)) return 'offline'

	const rawReason = roster && (roster.reason || roster.error || roster.message)
	const normalizedReason = String(rawReason || '').toLowerCase()
	if (normalizedReason.includes('terminal query failed') || normalizedReason.includes('offline') || normalizedReason.includes('econnrefused')) return 'offline'

	return ''
}

export const isCacheableFaceRosterPage = (roster) => Boolean(roster && faceRosterConnectionStatus(roster) !== 'offline')

export const cacheFaceRosterPageIfOnline = (deviceId, cursor, roster) => {
	if (isCacheableFaceRosterPage(roster)) pageCache.set(pageCacheKey(deviceId, cursor), { roster, storedAt: faceRosterPageCacheClock.now() })
}
